import {
  Box,
  Card,
  FormControl,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Select,
  TextField,
  Typography,
  Checkbox,
  Alert,
} from '@mui/material';
import Button from '@mui/material/Button';
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { configureCustomFieldConfigs } from './adminPageRequest';
import { LoadingButton } from '@mui/lab';
import { trackerToolbarNeutralOutlineColor } from '../TrackerFilter/trackerFilterToolbarStyles';
import { adminCardSx, adminCardWrapperSx, adminListScrollSx } from './adminStyles';

interface CustomFieldsContainerProps {
  expertCustomFields: CustomFieldConfig[];
  journalistCustomFields: CustomFieldConfig[];
  outletCustomFields: CustomFieldConfig[];
  communityMemberCustomFields: CustomFieldConfig[];
  organizationCustomFields: CustomFieldConfig[];
  audienceCustomFields: CustomFieldConfig[];
  showEngagementCustomFields: boolean;
  showAudienceCustomFields: boolean;
}

type CustomFieldConfigAction = 'add' | 'update' | 'remove';
interface CustomFieldConfig {
  action?: CustomFieldConfigAction;
  fieldKey: string;
  label: string;
  enabled: boolean;
}

interface CustomFieldsConfigCardProps {
  isEdit: boolean;
  index: number;
  key: string;
  configData: CustomFieldConfig;
  onLabelChange: (label: string, fieldKey: string) => void;
  onToggleEnabled: (enabled: boolean, fieldKey: string) => void;
  onRemove: (fieldKey: string) => void;
}

const CustomFieldsConfigListItem = ({
  isEdit,
  index,
  key,
  configData,
  onLabelChange,
  onToggleEnabled,
  onRemove,
}: CustomFieldsConfigCardProps) => {
  if (!isEdit) {
    return (
      <ListItem key={key} divider={true}>
        <Box mx={2} my={1} gap={2} display="flex" alignItems="center" justifyContent="space-between" width="100%">
          <Typography sx={{ fontWeight: 600 }} data-cy={`config-label-${index}`}>
            {configData.label}
          </Typography>
          <Typography data-cy={`config-enabled-${index}`}>{configData.enabled ? 'Enabled' : 'Disabled'}</Typography>
        </Box>
      </ListItem>
    );
  }

  return (
    <ListItem key={key} divider={true}>
      <Box display="flex" mx={2} my={1} gap={2} alignItems="center">
        <Box>
          <TextField
            data-cy={`config-label-textfield-${index}`}
            placeholder="Label"
            value={configData.label}
            onChange={(e) => onLabelChange(e.target.value, configData.fieldKey)}
          />
        </Box>
        <Box display="flex" alignItems="center">
          <Checkbox
            data-cy={`config-enabled-checkbox-${index}`}
            checked={configData.enabled}
            onChange={(e) => onToggleEnabled(e.target.checked, configData.fieldKey)}
          />
          <Typography>{configData.enabled ? 'Enabled' : 'Disabled'}</Typography>
        </Box>
        <Button
          data-cy={`config-remove-btn-${index}`}
          color="error"
          variant="outlined"
          onClick={() => onRemove(configData.fieldKey)}
        >
          Delete
        </Button>
      </Box>
    </ListItem>
  );
};

const NEW_CONFIG_PREFIX_REGEX = /^new_config_\d+$/;
const CONFIG_LIMIT = 5;
const TOOLBAR_BUTTON_HEIGHT = '40px';

const CustomFieldsContainer = ({
  expertCustomFields,
  journalistCustomFields,
  outletCustomFields,
  communityMemberCustomFields,
  organizationCustomFields,
  audienceCustomFields,
  showEngagementCustomFields,
  showAudienceCustomFields,
}: CustomFieldsContainerProps) => {
  const [selectedModel, setSelectedModel] = useState<string>('Journalist');
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [expertConfigs, setExpertConfigs] = useState<CustomFieldConfig[]>(expertCustomFields);
  const [journalistConfigs, setJournalistConfigs] = useState(journalistCustomFields);
  const [outletConfigs, setOutletConfigs] = useState(outletCustomFields);
  const [communityMemberConfigs, setCommunityMemberConfigs] = useState(communityMemberCustomFields);
  const [organizationConfigs, setOrganizationConfigs] = useState(organizationCustomFields);
  const [audienceConfigs, setAudienceConfigs] = useState(audienceCustomFields);

  const [removingExpertConfigs, setRemovingExpertConfigs] = useState<string[]>([]);
  const [removingJournalistConfigs, setRemovingJournalistConfigs] = useState<string[]>([]);
  const [removingOutletConfigs, setRemovingOutletConfigs] = useState<string[]>([]);
  const [removingCommunityMemberConfigs, setRemovingCommunityMemberConfigs] = useState<string[]>([]);
  const [removingOrganizationConfigs, setRemovingOrganizationConfigs] = useState<string[]>([]);
  const [removingAudienceConfigs, setRemovingAudienceConfigs] = useState<string[]>([]);

  const queryClient = useQueryClient();

  const resetCustomFieldConfigsStates = () => {
    setRemovingExpertConfigs([]);
    setRemovingJournalistConfigs([]);
    setRemovingOutletConfigs([]);
    setRemovingCommunityMemberConfigs([]);
    setRemovingOrganizationConfigs([]);
    setRemovingAudienceConfigs([]);

    setExpertConfigs(expertCustomFields);
    setJournalistConfigs(journalistCustomFields);
    setOutletConfigs(outletCustomFields);
    setCommunityMemberConfigs(communityMemberCustomFields);
    setOrganizationConfigs(organizationCustomFields);
    setAudienceConfigs(audienceCustomFields);
  };

  const configureExpertConfigsMutation = useMutation({
    mutationFn: async (updates: any) => {
      await configureCustomFieldConfigs('Expert', updates);
      resetCustomFieldConfigsStates();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['expertCustomFields', 'adminPage'] });
    },
  });

  const configureJournalistConfigsMutation = useMutation({
    mutationFn: async (updates: any) => {
      await configureCustomFieldConfigs('Journalist', updates);
      resetCustomFieldConfigsStates();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['journalistCustomFields', 'adminPage'] });
    },
  });

  const configureOutletConfigsMutation = useMutation({
    mutationFn: async (updates: any) => {
      await configureCustomFieldConfigs('Org', updates);
      resetCustomFieldConfigsStates();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['outletCustomFields', 'adminPage'] });
    },
  });

  const configureCommunityMemberConfigsMutation = useMutation({
    mutationFn: async (updates: any) => {
      await configureCustomFieldConfigs('CommunityMember', updates);
      resetCustomFieldConfigsStates();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['communityMemberCustomFields', 'adminPage'] });
    },
  });

  const configureOrganizationConfigsMutation = useMutation({
    mutationFn: async (updates: any) => {
      await configureCustomFieldConfigs('Organization', updates);
      resetCustomFieldConfigsStates();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['organizationCustomFields', 'adminPage'] });
    },
  });

  const configureAudienceConfigsMutation = useMutation({
    mutationFn: async (updates: any) => {
      await configureCustomFieldConfigs('Audience', updates);
      resetCustomFieldConfigsStates();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['audienceCustomFields', 'adminPage'] });
    },
  });

  useEffect(() => {
    setExpertConfigs(expertCustomFields);
    setJournalistConfigs(journalistCustomFields);
    setOutletConfigs(outletCustomFields);
    setCommunityMemberConfigs(communityMemberCustomFields);
    setOrganizationConfigs(organizationCustomFields);
    setAudienceConfigs(audienceCustomFields);
  }, [
    expertCustomFields,
    journalistCustomFields,
    outletCustomFields,
    communityMemberCustomFields,
    organizationCustomFields,
    audienceCustomFields,
  ]);

  useEffect(() => {
    const engagementModels = ['Community Member', 'Organization'];

    if (!showEngagementCustomFields && engagementModels.includes(selectedModel)) {
      setSelectedModel('Journalist');
      setIsEdit(false);
    }

    if (!showAudienceCustomFields && selectedModel === 'Audience') {
      setSelectedModel('Journalist');
      setIsEdit(false);
    }
  }, [selectedModel, showAudienceCustomFields, showEngagementCustomFields]);

  const handleSelectChange = (selected: string) => {
    setSelectedModel(selected);
  };

  const handleConfigureBtnClick = () => {
    setIsEdit(true);
  };

  const getSelectedConfigs = () => {
    let list: CustomFieldConfig[] = [];
    switch (selectedModel) {
      case 'Expert':
        list = expertConfigs;
        break;
      case 'Journalist':
        list = journalistConfigs;
        break;
      case 'Outlet':
        list = outletConfigs;
        break;
      case 'Community Member':
        list = communityMemberConfigs;
        break;
      case 'Organization':
        list = organizationConfigs;
        break;
      case 'Audience':
        list = audienceConfigs;
        break;
      default:
        break;
    }
    return list;
  };

  const getAddingConfigs = () => {
    return getSelectedConfigs()
      .filter((c) => c.action === 'add')
      .filter((c) => c.label)
      .map((c) => ({
        label: c.label,
        enabled: c.enabled,
      }));
  };

  const getUpdatingConfigs = () => {
    return getSelectedConfigs()
      .filter((c) => c.action === 'update')
      .map((c) => ({
        fieldKey: c.fieldKey,
        label: c.label,
        enabled: c.enabled,
      }));
  };

  const getRemovingConfigs = () => {
    let list: string[] = [];
    switch (selectedModel) {
      case 'Expert':
        list = removingExpertConfigs;
        break;
      case 'Journalist':
        list = removingJournalistConfigs;
        break;
      case 'Outlet':
        list = removingOutletConfigs;
        break;
      case 'Community Member':
        list = removingCommunityMemberConfigs;
        break;
      case 'Organization':
        list = removingOrganizationConfigs;
        break;
      case 'Audience':
        list = removingAudienceConfigs;
        break;
      default:
        break;
    }
    return list.map((k) => ({ fieldKey: k }));
  };

  const handleSaveBtnClick = () => {
    const adds = getAddingConfigs();
    const updates = getUpdatingConfigs();
    const removes = getRemovingConfigs();

    switch (selectedModel) {
      case 'Expert':
        configureExpertConfigsMutation
          .mutateAsync({
            adds,
            updates,
            removes,
          })
          .then(() => {
            setIsEdit(false);
          });
        break;
      case 'Journalist':
        configureJournalistConfigsMutation
          .mutateAsync({
            adds,
            updates,
            removes,
          })
          .then(() => {
            setIsEdit(false);
          });
        break;
      case 'Outlet':
        configureOutletConfigsMutation
          .mutateAsync({
            adds,
            updates,
            removes,
          })
          .then(() => {
            setIsEdit(false);
          });
        break;
      case 'Community Member':
        configureCommunityMemberConfigsMutation
          .mutateAsync({
            adds,
            updates,
            removes,
          })
          .then(() => {
            setIsEdit(false);
          });
        break;
      case 'Organization':
        configureOrganizationConfigsMutation
          .mutateAsync({
            adds,
            updates,
            removes,
          })
          .then(() => {
            setIsEdit(false);
          });
        break;
      case 'Audience':
        configureAudienceConfigsMutation
          .mutateAsync({
            adds,
            updates,
            removes,
          })
          .then(() => {
            setIsEdit(false);
          });
        break;
      default:
        console.error('save failed - unknown model selected');
    }
  };

  const handleCancelBtnClick = () => {
    resetCustomFieldConfigsStates();
    setIsEdit(false);
  };

  const getCustomFieldConfigsListItemLabelChangeEventHandler = () => {
    const configsChangeMapper = (c: CustomFieldConfig, label: string, fieldKey: string): CustomFieldConfig => {
      if (c.fieldKey === fieldKey && NEW_CONFIG_PREFIX_REGEX.test(c.fieldKey)) {
        return {
          ...c,
          action: 'add',
          label: label,
        };
      } else if (c.fieldKey === fieldKey && !NEW_CONFIG_PREFIX_REGEX.test(c.fieldKey)) {
        return {
          ...c,
          action: 'update',
          label: label,
        };
      } else {
        return c;
      }
    };

    switch (selectedModel) {
      case 'Expert':
        return (label: string, fieldKey: string) =>
          setExpertConfigs(expertConfigs.map((c) => configsChangeMapper(c, label, fieldKey)));
      case 'Journalist':
        return (label: string, fieldKey: string) =>
          setJournalistConfigs(journalistConfigs.map((c) => configsChangeMapper(c, label, fieldKey)));
      case 'Outlet':
        return (label: string, fieldKey: string) =>
          setOutletConfigs(outletConfigs.map((c) => configsChangeMapper(c, label, fieldKey)));
      case 'Community Member':
        return (label: string, fieldKey: string) =>
          setCommunityMemberConfigs(communityMemberConfigs.map((c) => configsChangeMapper(c, label, fieldKey)));
      case 'Organization':
        return (label: string, fieldKey: string) =>
          setOrganizationConfigs(organizationConfigs.map((c) => configsChangeMapper(c, label, fieldKey)));
      case 'Audience':
        return (label: string, fieldKey: string) =>
          setAudienceConfigs(audienceConfigs.map((c) => configsChangeMapper(c, label, fieldKey)));
      default:
        return (label: string, fieldKey: string) => {
          console.error('unknown selected model, cannot update label');
        };
    }
  };

  const getCustomFieldConfigsListItemToggleEnabledEventHandler = () => {
    const configsChangeMapper = (c: CustomFieldConfig, enabled: boolean, fieldKey: string): CustomFieldConfig => {
      if (c.fieldKey === fieldKey && NEW_CONFIG_PREFIX_REGEX.test(c.fieldKey)) {
        return {
          ...c,
          action: 'add',
          enabled: enabled,
        };
      } else if (c.fieldKey === fieldKey && !NEW_CONFIG_PREFIX_REGEX.test(c.fieldKey)) {
        return {
          ...c,
          action: 'update',
          enabled: enabled,
        };
      } else {
        return c;
      }
    };

    switch (selectedModel) {
      case 'Expert':
        return (enabled: boolean, fieldKey: string) => {
          setExpertConfigs(expertConfigs.map((c) => configsChangeMapper(c, enabled, fieldKey)));
        };
      case 'Journalist':
        return (enabled: boolean, fieldKey: string) => {
          setJournalistConfigs(journalistConfigs.map((c) => configsChangeMapper(c, enabled, fieldKey)));
        };
      case 'Outlet':
        return (enabled: boolean, fieldKey: string) => {
          setOutletConfigs(outletConfigs.map((c) => configsChangeMapper(c, enabled, fieldKey)));
        };
      case 'Community Member':
        return (enabled: boolean, fieldKey: string) => {
          setCommunityMemberConfigs(communityMemberConfigs.map((c) => configsChangeMapper(c, enabled, fieldKey)));
        };
      case 'Organization':
        return (enabled: boolean, fieldKey: string) => {
          setOrganizationConfigs(organizationConfigs.map((c) => configsChangeMapper(c, enabled, fieldKey)));
        };
      case 'Audience':
        return (enabled: boolean, fieldKey: string) => {
          setAudienceConfigs(audienceConfigs.map((c) => configsChangeMapper(c, enabled, fieldKey)));
        };
      default:
        return (enabled: boolean, fieldKey: string) => {
          console.error('unknown selected model, cannot toggle enabled/disabled');
        };
    }
  };

  const getCustomFieldConfigsListItemRemoveEventHandler = () => {
    switch (selectedModel) {
      case 'Expert':
        return (fieldKey: string) => {
          setExpertConfigs(expertConfigs.filter((c) => c.fieldKey !== fieldKey));
          if (!NEW_CONFIG_PREFIX_REGEX.test(fieldKey)) {
            setRemovingExpertConfigs([...removingExpertConfigs, fieldKey]);
          }
        };
      case 'Journalist':
        return (fieldKey: string) => {
          setJournalistConfigs(journalistConfigs.filter((c) => c.fieldKey !== fieldKey));
          if (!NEW_CONFIG_PREFIX_REGEX.test(fieldKey)) {
            setRemovingJournalistConfigs([...removingJournalistConfigs, fieldKey]);
          }
        };
      case 'Outlet':
        return (fieldKey: string) => {
          setOutletConfigs(outletConfigs.filter((c) => c.fieldKey !== fieldKey));
          if (!NEW_CONFIG_PREFIX_REGEX.test(fieldKey)) {
            setRemovingOutletConfigs([...removingOutletConfigs, fieldKey]);
          }
        };
      case 'Community Member':
        return (fieldKey: string) => {
          setCommunityMemberConfigs(communityMemberConfigs.filter((c) => c.fieldKey !== fieldKey));
          if (!NEW_CONFIG_PREFIX_REGEX.test(fieldKey)) {
            setRemovingCommunityMemberConfigs([...removingCommunityMemberConfigs, fieldKey]);
          }
        };
      case 'Organization':
        return (fieldKey: string) => {
          setOrganizationConfigs(organizationConfigs.filter((c) => c.fieldKey !== fieldKey));
          if (!NEW_CONFIG_PREFIX_REGEX.test(fieldKey)) {
            setRemovingOrganizationConfigs([...removingOrganizationConfigs, fieldKey]);
          }
        };
      case 'Audience':
        return (fieldKey: string) => {
          setAudienceConfigs(audienceConfigs.filter((c) => c.fieldKey !== fieldKey));
          if (!NEW_CONFIG_PREFIX_REGEX.test(fieldKey)) {
            setRemovingAudienceConfigs([...removingAudienceConfigs, fieldKey]);
          }
        };
      default:
        return (fieldKey: string) => {
          console.error('unknown selected model, cannot remove custom field config');
        };
    }
  };

  const handleAddNewConfigClick = () => {
    switch (selectedModel) {
      case 'Expert':
        setExpertConfigs([
          ...expertConfigs,
          {
            action: 'add',
            fieldKey: `new_config_${Date.now()}`,
            label: '',
            enabled: true,
          },
        ]);
        break;
      case 'Journalist':
        setJournalistConfigs([
          ...journalistConfigs,
          {
            action: 'add',
            fieldKey: `new_config_${Date.now()}`,
            label: '',
            enabled: true,
          },
        ]);
        break;
      case 'Outlet':
        setOutletConfigs([
          ...outletConfigs,
          {
            action: 'add',
            fieldKey: `new_config_${Date.now()}`,
            label: '',
            enabled: true,
          },
        ]);
        break;
      case 'Community Member':
        setCommunityMemberConfigs([
          ...communityMemberConfigs,
          {
            action: 'add',
            fieldKey: `new_config_${Date.now()}`,
            label: '',
            enabled: true,
          },
        ]);
        break;
      case 'Organization':
        setOrganizationConfigs([
          ...organizationConfigs,
          {
            action: 'add',
            fieldKey: `new_config_${Date.now()}`,
            label: '',
            enabled: true,
          },
        ]);
        break;
      case 'Audience':
        setAudienceConfigs([
          ...audienceConfigs,
          {
            action: 'add',
            fieldKey: `new_config_${Date.now()}`,
            label: '',
            enabled: true,
          },
        ]);
        break;
      default:
        break;
    }
  };

  const isSavingConfigs =
    configureExpertConfigsMutation.isPending ||
    configureJournalistConfigsMutation.isPending ||
    configureOutletConfigsMutation.isPending ||
    configureCommunityMemberConfigsMutation.isPending ||
    configureOrganizationConfigsMutation.isPending ||
    configureAudienceConfigsMutation.isPending;

  let actionComponent;
  if (isEdit) {
    actionComponent = (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <LoadingButton
          loading={isSavingConfigs}
          sx={{ height: TOOLBAR_BUTTON_HEIGHT }}
          color="primary"
          variant="contained"
          onClick={() => handleSaveBtnClick()}
          data-cy="save-btn"
        >
          Save
        </LoadingButton>
        <Button
          disabled={isSavingConfigs}
          variant="outlined"
          sx={{ height: TOOLBAR_BUTTON_HEIGHT }}
          onClick={() => handleCancelBtnClick()}
          data-cy="cancel-btn"
        >
          Cancel
        </Button>
      </Box>
    );
  } else {
    actionComponent = (
      <Button
        color="primary"
        variant="contained"
        sx={{ height: TOOLBAR_BUTTON_HEIGHT }}
        onClick={() => handleConfigureBtnClick()}
        data-cy="configure-btn"
      >
        Configure
      </Button>
    );
  }

  const addNewCustomConfigButton = (
    <Button
      sx={{ width: 'calc(100% - 32px)', mx: 2, my: 1 }}
      variant="outlined"
      onClick={() => handleAddNewConfigClick()}
      data-cy="add-btn"
    >
      Add
    </Button>
  );

  return (
    <Box sx={adminCardWrapperSx}>
      <Card sx={adminCardSx}>
        <Box
          sx={{
            ...adminListScrollSx,
            display: 'flex',
            flexDirection: 'column',
            pt: 3,
            pb: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
              pb: 1,
              px: 2,
            }}
          >
            <FormControl size="small" sx={{ minWidth: '200px' }}>
              <InputLabel id="custom-field-config-model-label">Contact Type</InputLabel>
              <Select
                labelId="custom-field-config-model-label"
                id="custom-field-config-model-select"
                label="Contact Type"
                value={selectedModel}
                onChange={(e) => handleSelectChange(e.target.value)}
                sx={{
                  height: TOOLBAR_BUTTON_HEIGHT,
                  borderRadius: 1,
                  backgroundColor: '#FFFFFF',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: trackerToolbarNeutralOutlineColor,
                  },
                }}
              >
                <MenuItem value={'Journalist'}>Journalist</MenuItem>
                <MenuItem value={'Expert'}>Expert</MenuItem>
                <MenuItem value={'Outlet'}>Outlet</MenuItem>
                {showEngagementCustomFields && <MenuItem value={'Community Member'}>Community Member</MenuItem>}
                {showEngagementCustomFields && <MenuItem value={'Organization'}>Organization</MenuItem>}
                {showAudienceCustomFields && <MenuItem value={'Audience'}>Audience</MenuItem>}
              </Select>
            </FormControl>
            {actionComponent}
          </Box>

          {isEdit && (
            <Alert color="info" sx={{ mx: 2, mb: 1 }}>
              <Typography>
                You can add up to <strong>5</strong> custom fields. Renaming a field will not affect its existing values
                in {selectedModel}. To reset a field's values, delete it and create a new one in its place. Disabled
                fields will be hidden from {selectedModel}.
              </Typography>
            </Alert>
          )}

          <List disablePadding sx={{ width: '100%' }}>
            {getSelectedConfigs().map((config, index) => (
              <CustomFieldsConfigListItem
                isEdit={isEdit}
                index={index}
                key={config.fieldKey}
                configData={config}
                onLabelChange={getCustomFieldConfigsListItemLabelChangeEventHandler()}
                onToggleEnabled={getCustomFieldConfigsListItemToggleEnabledEventHandler()}
                onRemove={getCustomFieldConfigsListItemRemoveEventHandler()}
              />
            ))}
            {getSelectedConfigs().length < CONFIG_LIMIT && isEdit && addNewCustomConfigButton}
          </List>
        </Box>
      </Card>
    </Box>
  );
};

export default CustomFieldsContainer;
